import { computed, CSSProperties, ComputedRef, Ref } from 'vue';
import { merge } from 'lodash-es';
import { numberWithComma, getRoundedNumberWithComma } from '@/common/utils/core/number/format';
import type { Props } from '../../modernGrid.setup';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridColumnInstance,
} from '../../modernGrid.types';

export const useSummary = <RowDataType extends GridRawRowDataType>({
  props,
  table,
  tableColumns,
}: {
  props: Props<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  tableColumns: ComputedRef<GridColumnInstance<RowDataType>[]>;
}) => {
  const summaryInfo = computed(() => merge({}, { use: false }, props.option?.summary));

  const calculateSummaryValue = (
    columnId: string,
    summaryType: 'sum' | 'average' | 'max' | 'min' | 'count',
  ): number => {
    const rows = table.value?.getFilteredRowModel().rows ?? [];
    const values = rows
      .map((row) => {
        const value = row.original[columnId];
        return typeof value === 'number' ? value : NaN;
      })
      .filter((v) => !Number.isNaN(v));

    if (values.length === 0) {
      return summaryType === 'count' ? 0 : 0;
    }

    switch (summaryType) {
      case 'sum':
        return values.reduce((acc, val) => acc + val, 0);
      case 'average':
        return values.reduce((acc, val) => acc + val, 0) / values.length;
      case 'max':
        return Math.max(...values);
      case 'min':
        return Math.min(...values);
      case 'count':
        return values.length;
      default:
        return 0;
    }
  };

  const formatSummaryValue = (
    value: number,
    summaryRenderer: string | undefined,
    decimal?: number,
  ): string => {
    const formattedValue =
      decimal !== undefined ? getRoundedNumberWithComma(value, decimal) : numberWithComma(value);

    if (!summaryRenderer) {
      return formattedValue;
    }

    if (summaryRenderer.includes('{0}')) {
      return summaryRenderer.replace('{0}', formattedValue);
    }

    return summaryRenderer;
  };

  const getSummaryDisplayValue = (columnId: string): string => {
    const column = tableColumns.value.find((col) => col.id === columnId);
    if (!column) return '';

    const meta = column.columnDef.meta;
    const { summaryType, summaryRenderer, decimal } = meta || {};

    if (summaryType) {
      const value = calculateSummaryValue(columnId, summaryType);
      return formatSummaryValue(value, summaryRenderer, decimal);
    }

    if (summaryRenderer) {
      return summaryRenderer;
    }

    return '';
  };

  const getSummaryCellAlignClass = (columnId: string): string => {
    const column = tableColumns.value.find((col) => col.id === columnId);
    if (!column) return '';

    const meta = column.columnDef.meta;
    const { align, type, summaryType, summaryRenderer } = meta || {};

    if (summaryRenderer && !summaryType) {
      return 'left-align';
    }

    if (type === 'number' || type === 'float' || type === 'age' || summaryType) {
      return 'right-align';
    }

    if (align) {
      return `${align}-align`;
    }

    return '';
  };

  const getSummaryCellStyle = (columnId: string): CSSProperties => {
    const column = tableColumns.value.find((col) => col.id === columnId);
    if (!column) return {};

    return {
      width: `${column.getSize()}px`,
    };
  };

  const hasSummaryRow = computed(() => {
    if (!summaryInfo.value.use) return false;

    const rows = table.value?.getFilteredRowModel().rows ?? [];
    if (rows.length === 0) return false;

    return tableColumns.value.some((col) => {
      const meta = col.columnDef.meta;
      return meta?.summaryType || meta?.summaryRenderer;
    });
  });

  return {
    summaryInfo,
    hasSummaryRow,
    getSummaryDisplayValue,
    getSummaryCellAlignClass,
    getSummaryCellStyle,
  };
};
